import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { IBusinessSitesFilters } from 'features/BusinessSites/types'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'
import { isEmpty, keys } from 'lodash'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { IBusinessClientAutocomplete } from 'features/BusinessClients/components/MultiBusinessClientAutocomplete'
import { Formik } from 'formik'
import { Tag } from 'components/v2/Tag'

const messages = defineMessages({
  selectBusinessClient: {
    id: 'businessSites.filters.businessClient.label',
    defaultMessage: 'Select business client'
  }
})

const defaultValues: IBusinessSitesFilters = {
  businessClient: null
}

interface IBusinessSitesFiltersProps {
  filters: IBusinessSitesFilters
  onSubmit: (filters: IBusinessSitesFilters) => void
}

export const BusinessSitesFilters = ({ filters, onSubmit }: IBusinessSitesFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IBusinessSitesFilters = {
    businessClient: filters.businessClient || defaultValues.businessClient
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values } = props
        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }
        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'businessClient':
                          label = <FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />
                          value = (filterValue as IBusinessSitesFilters['businessClient'])?.name
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <BusinessClientAutocomplete
                    id="businessClient.id"
                    label={
                      <FormattedMessage
                        id="businessSites.filters.businessClient.label"
                        defaultMessage="Business client"
                      />
                    }
                    placeholder={intl.formatMessage(messages.selectBusinessClient)}
                    value={values.businessClient?.name || ''}
                    onChange={(businessClient: IBusinessClientAutocomplete): void => {
                      setFieldValue('businessClient', businessClient)
                      onSubmit({
                        ...values,
                        businessClient
                      })
                    }}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
